import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog } from '@mui/material';

import { loadCallOutcomes, loadWorkConfidence } from '../../../../../store/directusService';
import { updateIWSRecord } from '../../../../../store/participantService';
import { reverseFormatDate, todayDate } from '../../../../../utils/dateFunctions';
import { getObjFromId } from '../../../../../utils/directusFunctions';
import { hasRole } from '../../../../../utils/userRoles';
import Button from '../../../../formElements/Button';
import SingleSelect from '../../../../formElements/SingleSelect';
import StaticField from '../../../../formElements/StaticField';
import TextAreaField from '../../../../formElements/TextAreaField';
import { validate } from '../validation/validateCreateIwsCall';

import '../../../../../commonStyles/muiStyles.css';
import form from '../../../../../commonStyles/formStyles.module.css';
import modal from '../../../../../commonStyles/modal.module.css';

const _NOTES_LIMIT = 750;

const AddCallEditorModal = ({ isOpen, acceptedRoles, onOpenAddCallEditor, row, businessName }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        participantId: '',
        frequencyId: '',
        placementId: '',
        iwsEventOwnerId: '',
        frequencyNote: '',
        dueDate: '',
        frequencyWeekNo: null,
        callDate: '',
        workConfidenceId: '',
        callOutcomeId: '',
        note: '',
        notesFromLastCall: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [callOutcomesWithoutDue, setCallOutcomesWithoutDue] = useState([]);
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const callOutcomes = useSelector((state) => state.entities.directusService.callOutcomes);
    const workConfidence = useSelector((state) => state.entities.directusService.workConfidence);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        callOutcomes?.length < 1 && dispatch(loadCallOutcomes());
        workConfidence?.length < 1 && dispatch(loadWorkConfidence());
    }, []);

    useEffect(() => {
        setNewEntry(row);
    }, [row]);

    useEffect(() => {
        if (callOutcomes?.length < 1) return;
        setCallOutcomesWithoutDue(callOutcomes.filter((el) => el.name !== 'Due'));
    }, [callOutcomes]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === `IWS Record for ${businessName} has been updated`) clearData();
    }, [successMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const clearData = () => {
        setNewEntry(initialState);
        setErrors({});
        setIsUpdateButtonDisabled(false);
        onOpenAddCallEditor(false);
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onClose = (e, reason) => {
        if (reason === 'backdropClick') return;
        clearData();
    };

    const onCancel = () => clearData();

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateIWSRecord(newEntry, businessName));
    };

    // RENDER
    return (
        <Dialog
            className="muiDialogWrapper"
            open={isOpen}
            onClose={onClose}
            fullWidth
            aria-labelledby="form-dialog-title">
            <div className={modal.modalHeader}>
                <h3>{`Complete IWS Call: Week ${row.frequencyWeekNo || ''}`}</h3>
            </div>
            <div className={modal.modalContent}>
                <form
                    onSubmit={onSubmit}
                    className={form.formWrapper}
                    data-testid="add-iws-call-form">
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <SingleSelect
                                id="callOutcomeId"
                                label="Call Outcome"
                                placeholder="Select call outcome..."
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                customClass="absolutePosition"
                                menuItems={callOutcomesWithoutDue || []}
                                selectedId={newEntry.callOutcomeId}
                                selected={getObjFromId(callOutcomes, newEntry.callOutcomeId)}
                                error={errors.callOutcomeId}
                                onChange={(chosenId) => onUpdate('callOutcomeId', chosenId)}
                            />
                            <StaticField
                                id="dateRequested"
                                label="Date Of Call"
                                content={reverseFormatDate(todayDate())}
                            />
                        </div>
                        <div className={form.formColumn}>
                            <SingleSelect
                                id="workConfidenceId"
                                label="Work Confidence"
                                placeholder="Select work confidence..."
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                customClass="absolutePosition"
                                menuItems={workConfidence || []}
                                selectedId={newEntry.workConfidenceId}
                                selected={getObjFromId(workConfidence, newEntry.workConfidenceId)}
                                error={errors.workConfidenceId}
                                onChange={(chosenId) => onUpdate('workConfidenceId', chosenId)}
                            />
                        </div>
                    </div>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <TextAreaField
                                id="note"
                                label="Notes"
                                placeholder="Enter In Work Support call notes"
                                mandatory={true}
                                disabled={!hasRole(acceptedRoles, roles)}
                                maxLength={_NOTES_LIMIT}
                                value={newEntry.note || ''}
                                count={`${newEntry.note?.length || 0}/${_NOTES_LIMIT}`}
                                error={errors.note}
                                onChange={(e) => onUpdate('note', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <StaticField
                                id={'notesFromLastCall'}
                                label={'Notes From Last Call'}
                                content={row?.notesFromLastCall || 'No notes from last call'}
                            />
                        </div>
                    </div>

                    <div className={modal.actionButtons}>
                        <Button
                            id="updateAddOwnPlacementButton"
                            disabled={!hasRole(acceptedRoles, roles) || isUpdateButtonDisabled}
                            content={'Update'}
                            error={errors.button}
                            clearError={() => clearError('button')}
                        />
                        <Button
                            id="updateAddOwnPlacementCancelButton"
                            type="button"
                            content="Cancel"
                            onClick={onCancel}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

AddCallEditorModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    acceptedRoles: PropTypes.arrayOf(PropTypes.string),
    onOpenAddCallEditor: PropTypes.func,
    row: PropTypes.object,
    businessName: PropTypes.string
};

export default AddCallEditorModal;
